import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { gauntlet, type PublicRequest } from '../lib/api';
import { cx, formatAmount, shortenAddress, timeAgo } from '../lib/format';
import { CopyButton } from '../components/ui';

/**
 * The public payment request page.
 *
 * No login, no account — this is the shareable link. It polls every few
 * seconds while the request is open and flips the moment the mempool sweep
 * sees a payment, long before confirmation.
 */
export function PublicRequestPage() {
  const { slug = '' } = useParams();

  const { data, error, isLoading } = useQuery({
    queryKey: ['public-request', slug],
    queryFn: () => gauntlet.publicRequest(slug),
    refetchInterval: (query) => {
      const status = (query.state.data as PublicRequest | undefined)?.status;
      return status === 'open' ? 5_000 : 30_000;
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-void">
        <Loader2 size={20} className="animate-spin text-ink-faint" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-void px-4">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">Payment request not found</h1>
          <p className="mt-2 text-sm text-ink-faint">
            The link may have expired or been removed. Ask the person who sent it for a new one.
          </p>
        </div>
      </div>
    );
  }

  const goal = BigInt(data.amount);
  const confirmed = BigInt(data.confirmedTotal);
  const progress =
    goal > 0n ? Math.min(100, Number((confirmed * 100n) / goal)) : 0;
  const paid = data.status === 'paid' || data.status === 'completed';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-void px-4 py-10">
      <div className="w-full max-w-md space-y-5">
        <div className="space-y-1 text-center">
          <p className="text-xs uppercase tracking-widest text-ink-faint">Payment request</p>
          <h1 className="text-xl font-semibold tracking-tight">{data.title}</h1>
          {data.note && <p className="text-sm text-ink-dim">{data.note}</p>}
        </div>

        {/* Status banner */}
        <div
          className={cx(
            'flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium',
            paid
              ? 'border-gain/40 bg-gain/10 text-gain'
              : data.pendingTotal !== '0'
                ? 'border-brass/40 bg-brass-wash text-brass'
                : 'border-hairline text-ink-dim',
          )}
        >
          {paid ? (
            <>
              <CheckCircle2 size={16} />
              {data.mode === 'multi' ? 'Goal reached — thank you!' : 'Paid — thank you!'}
            </>
          ) : data.pendingTotal !== '0' ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Payment detected — waiting for confirmation
            </>
          ) : data.status === 'expired' ? (
            <>
              <Clock size={14} /> This request has expired
            </>
          ) : (
            <>
              <Clock size={14} />
              {data.mode === 'multi' ? 'Contributions open' : 'Waiting for payment'}
            </>
          )}
        </div>

        {/* The ask */}
        <div className="rounded-2xl border border-hairline bg-abyss p-6 text-center">
          <p className="label-xs text-ink-faint">
            {data.mode === 'multi' ? 'Goal' : 'Amount requested'}
          </p>
          <p className="num mt-1 text-3xl font-semibold tracking-tight">
            {formatAmount(data.amount, 8)}{' '}
            <span className="text-lg text-ink-faint">{data.symbol}</span>
          </p>

          {data.mode === 'multi' && (
            <div className="mt-4 space-y-1.5">
              <div className="h-2 overflow-hidden rounded-full bg-raised">
                <div
                  className="h-full rounded-full bg-brass transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="num text-[11px] text-ink-faint">
                {formatAmount(data.confirmedTotal, 8)} {data.symbol} of{' '}
                {formatAmount(data.amount, 8)} raised
              </p>
            </div>
          )}

          <div className="mt-5 flex justify-center">
            <div className="rounded-xl bg-white p-3">
              <QRCodeSVG value={data.uri} size={180} level="M" marginSize={0} />
            </div>
          </div>

          <p className="addr mt-4 break-all rounded-lg border border-hairline bg-void p-2.5 text-xs">
            {data.address}
          </p>
          <div className="mt-2 flex justify-center gap-2">
            <CopyButton value={data.address} label="Copy address" />
            <CopyButton value={data.uri} label="Copy payment link" />
          </div>
        </div>

        {/* Recent contributions */}
        {data.payments.length > 0 && (
          <div className="space-y-1.5">
            <p className="label-xs px-1">Recent payments</p>
            {data.payments.slice(0, 5).map((payment) => (
              <div
                key={payment.txid}
                className="flex items-center gap-2 rounded-lg border border-hairline px-3 py-2 text-xs"
              >
                <span
                  className={cx(
                    'size-1.5 shrink-0 rounded-full',
                    payment.confirmations < 1 ? 'bg-brass' : 'bg-gain',
                  )}
                />
                <span className="num">{formatAmount(payment.amount, 8)} {data.symbol}</span>
                <span className="text-ink-faint">{shortenAddress(payment.txid, 8, 6)}</span>
                <span className="ml-auto text-[10px] text-ink-faint">
                  {payment.confirmations < 1 ? 'unconfirmed' : 'confirmed'} ·{' '}
                  {timeAgo(payment.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-[11px] leading-relaxed text-ink-faint">
          Pay from any wallet by scanning the QR or copying the address. The status updates the
          moment the payment is broadcast — confirmations follow shortly after. Only pay people
          you know.
        </p>
      </div>
    </div>
  );
}
